import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxSpinnerService } from 'ngx-spinner';
import { CloudTypes } from 'src/app/core/models/globals';
import { StorageDataLevel, StorageDataType } from 'src/app/core/models/storage-data-type';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { StorageDataTransferService } from 'src/app/core/services/storage-data-transfer.service';
import { StorageOracleService } from 'src/app/core/services/storage-oracle.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { UserRolesInt } from '../../../../../core/models/users';
import { AwsCloudService } from '../../../../../core/services/aws-cloud.service';
import { EnvironmentService } from '../../../../../core/services/environment.service';
import { GoogleCloudService } from '../../../../../core/services/google-cloud.service';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';
import { AzureStorageType } from '../../../../../core/models/storage';
import { DeploymentService } from '../../../../../core/services/deployment.service';

@Component({
  selector: 'app-store-explorer',
  templateUrl: './store-explorer.component.html',
  styleUrls: ['./store-explorer.component.scss']
})
export class StoreExplorerComponent implements OnInit {

  storeItems: StorageDataType[];
  storeBreadCrumb: StorageDataLevel[];
  multiSelectIndex = [];
  files: string[] = [];
  depName: string = this.data.depName;
  dirName: string = 'syncstore';
  errorMsg: string;
  toShowError: boolean;
  cloudType = this.data.cloudType;
  loggedUser = this.authenticationService.currentUserValue.username?.split("@")[0];
  syncstorePath = this.env.envVariables.syncStoreRoute;
  currentPath: string = [CloudTypes.OnPremise.toString(), CloudTypes.Oracle.toString(), CloudTypes.Google].includes(this.cloudType) ? `${this.env.envVariables.syncStoreRoute}/${this.loggedUser}` : `./${this.loggedUser}`;
  toChooseSingle = this.data.id;
  toZipCheck: boolean = false;
  currentStorageName = this._headerService.getStorageNameForDeployment(this.depName);
  currentLoggedInUserRole: number;
  isBreadCrumbEditable: boolean = false;
  breadCrumbPath: string = "";
  storageSearch: string = "";
  selectAll: boolean = false;
  destroy$ = new Subject<void>();
  loadDataSubscription: Subscription;

  constructor(
    private storageService: StorageService,
    private spinner: NgxSpinnerService,
    @Inject(MAT_DIALOG_DATA) public data: { depName: string, id: string, cloudType: string },
    public storageDataTransferService: StorageDataTransferService,
    public dialog: MatDialogRef<StoreExplorerComponent>,
    private snackBar: MatSnackBar,
    private authenticationService: AuthenticationService,
    private env: EnvironmentService,
    private _oracleService: StorageOracleService,
    private _headerService: HeaderService,
    private googleCloudService: GoogleCloudService,
    private awsService: AwsCloudService,
    private deploymentService: DeploymentService,
    private headerService: HeaderService
  ) { }

  ngOnInit(): void {
    this.spinner.show('storeSpinner');
    this.currentLoggedInUserRole = this.headerService.getUserRoleForDeployment(this.depName);
    this.storeBreadCrumb = new Array(
      new StorageDataLevel(this.dirName, this.dirName, this.syncstorePath, ''),
      new StorageDataLevel(this.loggedUser, this.loggedUser, this.currentPath, 'badge'),
    );
    const lastUsedPath = this.storageService.getLastUsedUseSyncstorePath()
    if (lastUsedPath != null) {
      this.currentPath = lastUsedPath;
      this.currentPath = this.currentPath.replace("./", this.env.envVariables.syncStoreRoute + "/");
      if (this.cloudType == CloudTypes.MicrosoftAzure) {
        if (!this.currentPath.includes(this.env.envVariables.syncStoreRoute)) {
          this.currentPath = this.env.envVariables.syncStoreRoute + "/" + this.currentPath;
        }
      }
      this.breadCrumbPath = this.currentPath.replace("./", "");
      this.handleSearchedPath();
    }
    this.checkDataCache();
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.spinner.hide('storeSpinner');
    this.destroy$.next();
    this.destroy$.complete();
    if (this.loadDataSubscription) {
      this.loadDataSubscription.unsubscribe();
    }
  }

  checkDataCache() {
    if (this.storageService.checkIfStorageCacheExists(this.currentPath)) {
      this.storeItems = this.storageService.getStorageCacheForPath(this.currentPath);
      this.setDefaultBreadCrumbPath();
      this.toShowError = false;
      this.spinner.hide('storeSpinner');
    } else {
      this.spinner.show('storeSpinner');
    }
    this.loadData(this.currentPath);
  }

  loadData(path: string) {
    let PREFIX = "./";
    if (this.env.envVariables.restrictStorageAccess) {
      let tempPath = this.currentPath;

      if (this.cloudType == CloudTypes.MicrosoftAzure
        && this.currentPath != this.env.envVariables.syncStoreRoute
        && !this.currentPath.includes(this.env.envVariables.workStoreRoute)) {
        if (path.startsWith(PREFIX)) {
          // PREFIX is exactly at the beginning
          path = path.slice(PREFIX.length);
        }
        this.currentPath = this.env.envVariables.syncStoreRoute + '/' + path;
      }

      if (this.currentPath != this.env.envVariables.syncStoreRoute
        && !this.authorizeFolderAccess(this.currentPath)
        && !this.currentPath.includes(this.env.envVariables.workStoreRoute)
      ) {
        this.setErrorMessage(2);
        this.spinner.hide("storeSpinner");
        return;
      }

      this.currentPath = tempPath;
    }

    this.setDefaultBreadCrumbPath();
    if (this.cloudType == CloudTypes.OnPremise) {
      this.storageService.ListFiles(this.currentPath, this.depName)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            if (!data || data?.length === 0) {
              this.setErrorMessage(1);
              this.spinner.hide('storeSpinner');
              return;
            }
            this.storeItems = <StorageDataType[]>(
              JSON.parse(JSON.stringify(data))
            );
            this.storageService.setStorageCacheForPath(this.currentPath, this.storeItems);
            this.toShowError = false;
            this.spinner.hide('storeSpinner')
          }, error: (error) => {
            this.setErrorMessage(0);
            this.spinner.hide('storeSpinner');
          }
        })
    } else if (this.cloudType == CloudTypes.MicrosoftAzure) {
      if (!this.storageDataTransferService.serviceClient) {
        this.storageService
          .getStorageSasShare(this.depName, "default")
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (data: any) => {
              this.storageDataTransferService.azureFilesInitialize(data?.sas, data?.share)
                .then(data => {
                  this.loadAzurefileStorage(path);
                });
            }, error: (err) => {
              this.setErrorMessage(0);
              this.spinner.hide('storeSpinner');
            }
          });
      } else {
        this.loadAzurefileStorage(path);
      }
    } else if (this.cloudType == CloudTypes.Google) {
      this.googleCloudService.getStorageData(this.depName, this.currentStorageName, this.currentPath)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: any) => {
            if (data == undefined) {
              this.spinner.hide('storeSpinner');
            } else {
              if (data.length == 0) {
                this.setErrorMessage(1);
                this.spinner.hide('storeSpinner');
              } else {
                this.storeItems = data.map(file => {
                  return {
                    name: file.name,
                    type: file.type,
                    path: this.currentPath + `/${file.name}`,
                    value: file.value,
                    owner: file.owner,
                    date: file.date,
                    size: file.size
                  }
                });
                this.storageService.setStorageCacheForPath(this.currentPath, this.storeItems);
                this.toShowError = false;
                this.spinner.hide('storeSpinner');
              }
            }
          },
          error: (err) => {
            this.setErrorMessage(0);
            this.spinner.hide('storeSpinner');
          }
        })
    }
    else if (this.cloudType == CloudTypes.AWS) {
      this.awsService.getStorageData(this.depName, this.currentStorageName, this.currentPath)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: any) => {
            if (data == undefined) {
              this.spinner.hide('storeSpinner');
            } else {
              if (data.length == 0) {
                this.setErrorMessage(1);
                this.spinner.hide('storeSpinner');
              } else {
                this.storeItems = data.map(file => {
                  return {
                    name: file.name,
                    type: file.type,
                    path: this.currentPath + `/${file.name}`,
                    value: file.value,
                    owner: file.owner,
                    date: file.date,
                    size: file.size
                  }
                });
                this.storageService.setStorageCacheForPath(this.currentPath, this.storeItems);
                this.toShowError = false;
                this.spinner.hide('storeSpinner');
              }
            }
          },
          error: (err) => {
            this.setErrorMessage(0);
            this.spinner.hide('storeSpinner');
          }
        })
    } else if (this.cloudType == CloudTypes.Oracle) {
      this._oracleService.getStorageData(this.depName, this.currentStorageName, this.currentPath)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.storeItems = <StorageDataType[]>(
              JSON.parse(JSON.stringify(data))
            );
            this.storeItems = this.storeItems.map(file => {
              return {
                name: file.name,
                type: file.type,
                path: this.currentPath + `/${file.name}`,
                value: file.value,
                owner: file.owner,
                date: file.date,
                size: file.size
              }
            });
            this.storageService.setStorageCacheForPath(this.currentPath, this.storeItems);
            this.toShowError = false;
            this.spinner.hide('storeSpinner')
          }, error: (error) => {
            this.setErrorMessage(1);
            this.spinner.hide('storeSpinner');
          }
        })
    }
  }

  loadAzurefileStorage(path) {
    let deployment = this.deploymentService.getProfileInfoFromSubject(
      this.depName
    );
    if (deployment.storageType == AzureStorageType.TxtBlobStorage) {
      this.storageDataTransferService.listBlobs(path).subscribe({
        next: (data) => {
          if (!data || data.length == 0) {
            this.setErrorMessage(1);
            this.spinner.hide('storeSpinner');
            return;
          };
          this.storeItems = <StorageDataType[]>(
            JSON.parse(JSON.stringify(data))
          );
          this.storageService.setStorageCacheForPath(this.currentPath, this.storeItems);
          this.toShowError = false;
          this.spinner.hide('storeSpinner');
        },
        error: (err) => {
          this.spinner.hide("storageLoad");
        },
      });
    }
    else {
      if (path == this.env.envVariables.syncStoreRoute) {
        path = '.';
      }
      this.storageDataTransferService.azureFilesListFiles(path).then(
        data => {
          if (!data || data.length == 0) {
            this.setErrorMessage(1);
            this.spinner.hide('storeSpinner');
            return;
          };
          this.storeItems = <StorageDataType[]>(
            JSON.parse(JSON.stringify(data))
          );
          this.storageService.setStorageCacheForPath(this.currentPath, this.storeItems);
          this.toShowError = false;
          this.spinner.hide('storeSpinner');
        }
      );
    }
  }

  onNavigation(breadcrumbElement: StorageDataLevel, itemClicked: StorageDataType, i: number): void {
    this.spinner.show('storeSpinner')
    this.multiSelectIndex = []
    if (itemClicked) {
      this.onFileFolderClick(itemClicked, i)
    } else {
      this.onBreadCrumbClick(breadcrumbElement)
    }
  }

  onFileFolderClick(itemClicked: StorageDataType, i: number) {
    if (this.isBreadCrumbEditable) this.isBreadCrumbEditable = false;
    var path = itemClicked.path;
    if (path.slice(-1) == "/") {
      path = path.slice(0, -1);
    }
    var lastCrumb = this.storeBreadCrumb[this.storeBreadCrumb.length - 1];
    if (itemClicked.type != 'file') {
      this.currentPath = path;
    }
    if (lastCrumb.path !== path) {
      if (itemClicked.type == 'directory') {
        this.storeBreadCrumb.forEach((element) => {
          element.className = '';
        });

        //Push New Directory
        this.storageSearch = "";
        this.manageBreadCrumbPath(path)
      } else {
        // download 
        this.storageSearch = "";
        this.multiSelectIndex.push(i)
        this.spinner.hide('storeSpinner')
      }
      this.checkDataCache()
    } else {
      this.spinner.hide("storeSpinner");
    }
  }

  manageBreadCrumbPath(path: string) {
    path = path.replace("./", "");
    if (this.cloudType == CloudTypes.MicrosoftAzure) {
      if (!path.includes(this.env.envVariables.syncStoreRoute)) {
        path = this.env.envVariables.syncStoreRoute + "/" + path;
      }
    }
    let pathArr = path.split("/");
    let existingPath = this.storeBreadCrumb.map(data => data.path);
    if (this.storeBreadCrumb.length + 2 > pathArr.length - 2) {
      let newBreadCrumbs: StorageDataLevel[] = [];
      for (let i = 2; i < pathArr.length; i++) {
        let currPath = path.split("/").slice(0, i + 1).join("/");
        newBreadCrumbs.push(new StorageDataLevel(pathArr[i], pathArr[i], currPath, ""));
      }
      this.storeBreadCrumb = newBreadCrumbs
    } else {
      for (let i = 2; i < pathArr.length; i++) {
        let currPath = path.split("/").slice(0, i + 1).join("/");
        if (!existingPath.includes(currPath)) {
          this.storeBreadCrumb.push(new StorageDataLevel(pathArr[i], pathArr[i], currPath, ""));
        }
      }
    }
    this.storeBreadCrumb[this.storeBreadCrumb.length == 0 ? 0 : this.storeBreadCrumb.length - 1].className = "badge";
  }

  onBreadCrumbClick(breadcrumbElement: StorageDataLevel) {
    var path = breadcrumbElement.path;
    var name = breadcrumbElement.name;
    this.errorMsg = null;
    var lastCrumb = this.storeBreadCrumb[this.storeBreadCrumb.length - 1];
    if (lastCrumb.path !== path) { //Old Directory Click
      this.currentPath = path;
      this.storageSearch = "";
      const index = this.storeBreadCrumb.findIndex((c) => c.name == name);
      this.storeBreadCrumb[index].className = 'badge';
      this.storeBreadCrumb.splice(index + 1);
      this.checkDataCache()
    } else this.spinner.hide('storeSpinner')
  }

  activateClass(item: StorageDataType) {
    if (!this.multiSelectIndex.includes(item)) {
      if (this.toChooseSingle) this.multiSelectIndex.length = 0;
      this.multiSelectIndex.push(item)
    }
    else {
      let idx = this.multiSelectIndex.indexOf(item)
      this.multiSelectIndex.splice(idx, 1)
    }
  }

  onSelectedChange(event): void {
    this.files = event;
  }

  onCancel() {
    this.storageService.setLastUsedUseSyncstorePath(this.currentPath);
    this.dialog.close();
  }

  onSubmit() {
    if (this.multiSelectIndex.length == 0) {
      this.snackBar.open("No file selected", 'Ok', { duration: 5000 });
    };
    // let filteredItems: StorageDataType[] = this.storeItems.filter((v) => v.name.toLowerCase().indexOf(this.storageSearch.toLowerCase()) > -1 );
    this.storageService.setLastUsedUseSyncstorePath(this.currentPath);
    if (this.toChooseSingle) {
      let item = this.multiSelectIndex[0];

      if (this.cloudType == CloudTypes.OnPremise) {
        this.files.push(item.path)
      } else if (this.cloudType == CloudTypes.Oracle) {
        this.files.push(this._oracleService.arrangeDirPath(item.path))
      } else if (this.cloudType == CloudTypes.Google) {
        this.files.push(this.googleCloudService.arrangeDirPath(item.path));
      }
      else if (this.cloudType == CloudTypes.AWS) {
        this.files.push(this.awsService.arrangeDirPath(item.path));
      } else {
        this.files.push(this.env.envVariables.syncStoreRoute + "/" + item.path.replace("./", ""))
      }

      if (this.toZipCheck && this.files[0].includes(".zip")) {
        this.files[0] = `${this.files[0]}:Zipped`;
      }

      if (item.type == 'file') {
        this.dialog.close({ data: this.files })
      } else {
        this.snackBar.open("Only file selection is allowed", 'Ok', { duration: 5000 })
        this.files.length = 0;
      }
    } else {
      this.multiSelectIndex.forEach(item => {
        if (this.cloudType == CloudTypes.OnPremise) {
          this.files.push(item.path)
        } else if (this.cloudType == CloudTypes.MicrosoftAzure) {
          let path = `${this.env.envVariables.syncStoreRoute}/${item.path}`;
          this.files.push(path.replace("./", ""))
        } else if (this.cloudType == CloudTypes.Oracle) {
          this.files.push(this._oracleService.arrangeDirPath(item.path));
        }
        else if (this.cloudType == CloudTypes.Google) {
          this.files.push(this.googleCloudService.arrangeDirPath(item.path));
        }
        else if (this.cloudType == CloudTypes.AWS) {
          this.files.push(this.awsService.arrangeDirPath(item.path));
        }
      });
      this.dialog.close({ data: this.files })
    }
  }

  setErrorMessage(errNum: number) {
    if (errNum == 0) {
      this.errorMsg = "Folder not found.";
    }
    if (errNum == 1) {
      this.errorMsg = 'Directory is empty.';
    }
    if (errNum == 2) {
      this.errorMsg = 'You do not have permission to access this Folder.';
    }
    this.toShowError = true;
  }

  authorizeFolderAccess(path: string) {
    let userName = this.authenticationService.currentUserValue.username;
    userName = userName && userName?.indexOf('@') > -1 ? userName.split('@')[0] : userName;
    if (this.currentLoggedInUserRole == UserRolesInt.JobOperator) {
      let syncStorePath = this.env.envVariables.syncStoreRoute;
      if (!path.includes(syncStorePath + "/")) path = syncStorePath + "/" + path;
      if (syncStorePath != path && path.indexOf(`${syncStorePath}/${userName}`) < 0) {
        return false;
      }
    }
    return true;
  }

  handleSearchedPath() {
    this.spinner.show("storeSpinner");
    this.isBreadCrumbEditable = false;
    let isFilePath: boolean = false;
    let pathString = this.breadCrumbPath.trim();

    // remove last letter if it is "/"
    if (pathString.endsWith("/")) {
      pathString = pathString.slice(0, -1);
    }


    //check if path is empty
    if (pathString == "") {
      this.snackBar.open("Please provide path", "Ok", { duration: 3000 });
      this.breadCrumbPath = this.currentPath;
      this.spinner.hide("storeSpinner");
      return;
    }

    // check if path provided is file or folder
    let pathArr = pathString.split("/").map(p => p.trim()).filter(p => p != "");
    if (pathArr.length == 1) {
      pathString = this.env.envVariables.syncStoreRoute + "/" + pathArr[0];
      this.breadCrumbPath = this.currentPath;
      pathArr = pathString.split("/");
    }

    if (!pathString.includes(this.env.envVariables.syncStoreRoute)) {
      this.snackBar.open("Please provide valid path", "Ok", { duration: 3000 });
      this.breadCrumbPath = this.currentPath;
      this.spinner.hide("storeSpinner");
      return;
    }

    let pathArrLen = pathArr.length;
    if (pathArr[pathArrLen - 1] != "") {
      if (pathArr[pathArrLen - 1].includes(".")) {
        isFilePath = true;
      }
    }

    // if the path given contains file name then create new path till folderName
    let folderPath = pathString;
    if (isFilePath) {
      folderPath = pathArr.slice(0, pathArrLen - 1).join("/");
    }

    // create StorageDataType for path provided
    let storageTypeObj = new StorageDataType(isFilePath ? pathArr[pathArrLen - 2] : pathArr[pathArrLen - 1], "directory", folderPath);

    this.onFileFolderClick(storageTypeObj, 0)
  }

  setDefaultBreadCrumbPath() {
    this.breadCrumbPath = this.currentPath;
    if (this.cloudType == CloudTypes.MicrosoftAzure && this.currentPath.includes("./")) {
      this.breadCrumbPath = this.syncstorePath + "/" + this.currentPath.slice(2);
    }
  }

  resetStorageSearch() {
    this.storageSearch = "";
  }

  onSelectAllCheckBox(event) {
    if (event.target.checked) {
      this.selectAll = true;
      let filteredItems: StorageDataType[] = this.storeItems.filter((v) => v.name.toLowerCase().indexOf(this.storageSearch.toLowerCase()) > -1);
      for (let i = 0; i < filteredItems.length; i++) {
        if (!this.multiSelectIndex.includes(filteredItems[i])) {
          this.multiSelectIndex.push(filteredItems[i]);
        }
      }
    } else {
      this.multiSelectIndex = [];
      this.selectAll = false;
    }
  }

  onRefresh() {
    this.spinner.show('storeSpinner');
    this.loadData(this.currentPath);
  }
}
